import { useCallback, useMemo } from "react";

import moment from "moment";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import {
  BUDGET_MIX_TARGET_SPEND,
  useHiddenBudgetMixes,
} from "@features/mmm/hooks/use-hidden-budget-mixes";
import { type MMMClientCycle } from "@north-beam/nb-common";

import { InitialClientSelectedCycle } from "../mmm-provider";

import { useClientCycles } from "./use-client-cycles";

export const useSelectedCycle = () => {
  const [clientCycles] = useClientCycles();
  const [, , { setHiddenClientSettings, setBudgetStrategies }] =
    useHiddenBudgetMixes();
  const [selectedCycleId, setSelectedCycleId] = useQueryParam(
    "cycle",
    withDefault(StringParam, "-1"),
  );

  const selectedCycleFromCycles = useMemo(
    () => clientCycles.find((cycle) => cycle.id === selectedCycleId),
    [selectedCycleId, clientCycles],
  );

  const selectedCycle = useMemo(
    () =>
      selectedCycleFromCycles
        ? selectedCycleFromCycles
        : { ...InitialClientSelectedCycle, id: selectedCycleId },
    [selectedCycleFromCycles, selectedCycleId],
  );

  const setSelectedCycle = useCallback(
    (cycle: MMMClientCycle) => {
      setSelectedCycleId(cycle.id);
      setHiddenClientSettings({});
      setBudgetStrategies([BUDGET_MIX_TARGET_SPEND]);
    },
    [setBudgetStrategies, setHiddenClientSettings, setSelectedCycleId],
  );

  const is4WeekCycle = useMemo(() => {
    // Add 1 because diff is not inclusive
    const weeks =
      moment(selectedCycle.date_range.end_date).diff(
        moment(selectedCycle.date_range.start_date),
        "weeks",
      ) + 1;
    return weeks === 4;
  }, [selectedCycle.date_range]);

  const totalDaysInCycle = useMemo(() => {
    return (
      moment(selectedCycle.date_range.end_date).diff(
        moment(selectedCycle.date_range.start_date),
        "days",
      ) + 1
    );
  }, [selectedCycle.date_range]);

  return {
    selectedCycle,
    setSelectedCycle,
    is4WeekCycle,
    totalDaysInCycle,
  } as const;
};
